import axios from "axios";
import { clearAuth0SpaCache } from "../utils/auth0Cache";
import { tokenService } from "./tokenService";

const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
});

api.interceptors.request.use((config) => {
  const token = tokenService.getToken();
  if (token) config.headers.Authorization = `${token}`;
  return config;
});

function redirectToLoginAndClearLocal() {
  tokenService.clearToken();
  localStorage.removeItem("user_uuid");
  localStorage.removeItem("workspace_uuid");
  localStorage.removeItem("favoriteAccount");
  localStorage.removeItem("userId");
  clearAuth0SpaCache();
  const domain = process.env.REACT_APP_AUTH0_DOMAIN;
  const clientId = process.env.REACT_APP_AUTH0_CLIENT_ID;
  const returnTo = encodeURIComponent(window.location.origin);
  if (domain && clientId) {
    window.location.href = `https://${domain}/v2/logout?client_id=${clientId}&returnTo=${returnTo}`;
  } else {
    window.location.href = window.location.origin;
  }
}

/**
 * Checks whether a 403 response carries the PERM002 code, which means the
 * user's permissions changed server-side and the token must be fully
 * re-issued (a simple refresh is not enough).
 */
const isPerm002 = (error) => {
  if (error.response?.status !== 403) return false;
  const data = error.response?.data;
  const codes = data?.msgCode || data?.body?.msgCode || [];
  return codes.some((m) => m.code === "PERM002");
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (isPerm002(error)) {
      redirectToLoginAndClearLocal('PERM002');
      return new Promise(() => {});
    }

    if (
      (error.response?.status === 401 || error.response?.status === 403) &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;

      if (!tokenService.getToken()) {
        return Promise.reject(error);
      }

      try {
        const tenantUuid = localStorage.getItem("workspace_uuid");
        const newToken = await tokenService.refreshTokenAuth0(tenantUuid || undefined);
        api.defaults.headers.common["Authorization"] = newToken;
        originalRequest.headers.Authorization = newToken;
        return api(originalRequest);
      } catch (err) {
        redirectToLoginAndClearLocal(`refresh-failed-${error.response?.status}`);
        return new Promise(() => {});
      }
    }

    return Promise.reject(error);
  }
);

export default api;
